/**
 * Pure helpers + the shared data contract for {@link CaptureTimeline}.
 *
 * Everything here is React-free — time/string formatting, quality-chip
 * mapping, the two API surfaces → one sorted stream transform, and the
 * single action-eligibility predicate that both the inline icon column
 * and the popup menu read from (so they can't drift). Kept in its own
 * module so the component file is just the render surface, and so this
 * logic is unit-testable in isolation.
 */
import type * as baselinesApi from '../../api/baselines';
import type { SessionPipeline } from '../../api/sessionPipeline';
import type * as sessionsApi from '../../api/sessions';
import { formatDateTime } from '../../util/datetime';

export interface TimelineEvent {
  id: string;
  kind: 'session' | 'baseline';
  /** ISO timestamp. */
  capturedAt: string;
  participantUserId: string | null;
  participantLabel: string | null;
  /** The capture device's label (which tablet recorded it), when known —
   *  drives the "By device" grouping. Null on rows with no device_label. */
  deviceLabel: string | null;
  /** Sessions carry the procedure name; baselines do not. */
  procedureName: string | null;
  /** Optional human label from a split ("Closing", …); null otherwise. */
  label: string | null;
  /** ROLES-AND-GO: the role taken in this recording (sessions only). */
  roleName?: string | null;
  /** Duration in seconds — used for the tooltip text. */
  durationS: number | null;
  /**
   * S3 object key for the NDJSON recording, when uploaded. Sessions
   * gate their download click on this being non-null; baselines fall
   * back to a client-side JSON-row dump when it is null.
   */
  s3Key: string | null;
  /**
   * Capture-quality summary (ROADMAP CAPTURE-RATE-MONITOR) for sessions —
   * drives the quality badge + its per-sensor tooltip. Null for baselines
   * and for rows recorded before it shipped.
   */
  captureQuality: sessionsApi.CaptureQuality | null;
  /** Non-null when the capture is soft-deleted (disabled). */
  deletedAt: string | null;
  /**
   * Scoring gave up on this recording (review B-04). Not the same as "still
   * processing": the job burned through its retries, so nothing further will
   * happen without someone intervening. Always false for baselines, which
   * aren't scored, and undefined on rows built somewhere other than
   * `buildTimelineEvents` — read it as falsy, never assert on it.
   */
  processingFailed?: boolean;
  /** Why, verbatim from the worker — shown in the badge's tooltip. The two
   *  observed causes (a missing S3 object, a recording too short to filter)
   *  need opposite responses, so the badge alone would be a dead end. */
  processingError?: string | null;
  /** Where the session's data is on its way in (SESSION-PIPELINE) — drives
   *  the tile's status dot. Set by the page from `useSessionPipelines`;
   *  absent until it loads, and on baselines. */
  pipeline?: SessionPipeline | null;
  /**
   * Organizational context (RFC procedure-instance, Phase 1b), tagged by
   * a tenant_admin. `sessionKind` is the event nature (live_surgery /
   * simulation / training / assessment) — distinct from `kind` above,
   * which is session-vs-baseline. All null for baselines + untagged
   * sessions.
   */
  sessionKind: string | null;
  departmentId: string | null;
  locationId: string | null;
  externalCaseRef: string | null;
  /**
   * Display-only names, resolved from departmentId/locationId by the
   * caller that has the org lists (the tenant Activity page). Left
   * undefined elsewhere (e.g. the participant Dashboard) — the tile just
   * shows nothing then.
   */
  departmentName?: string | null;
  locationName?: string | null;
  /**
   * Original API row carried through so the caller can hand the
   * client-side JSON dump (for baselines) the full payload without
   * re-fetching. Sessions never use it.
   */
  raw: sessionsApi.CaptureSession | baselinesApi.Baseline;
}

/**
 * Build a single sorted-newest-first stream from the two API
 * surfaces. Centralised here so the per-participant TenantActivity
 * page and the participant Dashboard pull the same shape.
 */
export function buildTimelineEvents(
  sessions: sessionsApi.CaptureSession[],
  baselines: baselinesApi.Baseline[],
): TimelineEvent[] {
  const events: TimelineEvent[] = [
    ...sessions.map(
      (s): TimelineEvent => ({
        id: s.id,
        kind: 'session',
        capturedAt: s.started_at,
        participantUserId: s.participant_user_id,
        participantLabel: s.participant_label,
        deviceLabel: s.device_label ?? null,
        procedureName: s.procedure_name,
        label: s.label ?? null,
        roleName: s.role_name ?? null,
        durationS: secondsBetween(s.started_at, s.ended_at),
        s3Key: s.s3_key,
        captureQuality: s.capture_quality ?? null,
        deletedAt: s.deleted_at,
        processingFailed: !!s.processing_failed,
        processingError: s.processing_error ?? null,
        sessionKind: s.kind ?? null,
        departmentId: s.department_id ?? null,
        locationId: s.location_id ?? null,
        externalCaseRef: s.external_case_ref ?? null,
        raw: s,
      }),
    ),
    ...baselines.map(
      (b): TimelineEvent => ({
        id: b.id,
        kind: 'baseline',
        label: null,
        capturedAt: b.captured_at,
        participantUserId: b.participant_user_id,
        participantLabel: b.participant_label,
        deviceLabel: b.device_label ?? null,
        procedureName: null,
        durationS: b.duration_s,
        s3Key: b.s3_key,
        captureQuality: null,
        deletedAt: b.deleted_at,
        processingFailed: false,
        processingError: null,
        sessionKind: null,
        departmentId: null,
        locationId: null,
        externalCaseRef: null,
        raw: b,
      }),
    ),
  ];
  events.sort(
    (a, b) =>
      new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime(),
  );
  return events;
}

/** One device's slice of the timeline (the "By device" grouping). */
export interface DeviceGroup {
  key: string;
  label: string;
  events: TimelineEvent[];
}

/**
 * Bucket events by the capture device that recorded them (`deviceLabel`).
 * Rows with no device label fall under "Unknown device", which sorts last.
 * Shared by the tenant Activity page and the participant Dashboard so both
 * group identically. Events keep their newest-first order within a group.
 */
export function groupByDevice(events: TimelineEvent[]): DeviceGroup[] {
  const UNKNOWN = 'Unknown device';
  const buckets = new Map<string, TimelineEvent[]>();
  for (const event of events) {
    const key = event.deviceLabel ?? UNKNOWN;
    const list = buckets.get(key);
    if (list) list.push(event);
    else buckets.set(key, [event]);
  }
  return [...buckets.entries()]
    .map(([label, evts]) => ({ key: label, label, events: evts }))
    .sort((a, b) => {
      if (a.key === UNKNOWN) return 1;
      if (b.key === UNKNOWN) return -1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
}

function secondsBetween(startedAt: string, endedAt: string): number | null {
  const a = new Date(startedAt).getTime();
  const b = new Date(endedAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.max(0, Math.round((b - a) / 1000));
}

/**
 * Format a timestamp into two short lines for under-the-icon label:
 *   line 1 — calendar date ("Jun 10", or "Jun 10 2025" when older)
 *   line 2 — time of day ("10:34")
 *
 * Reviewers asked for the actual date over the prior relative-day
 * form — at a glance "Mon" doesn't tell you which Monday. Time
 * lives on a second line so the date is the dominant token.
 */
export function formatTimelineLabel(
  iso: string,
  timeZone?: string,
): { date: string; time: string } {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return { date: iso, time: '' };
  const now = new Date();
  const sameYear = t.getFullYear() === now.getFullYear();
  const date = t.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone,
  });
  // Label the zone so the tile matches the tooltip (and the tenant's setting)
  // rather than reading as a bare, ambiguous local time.
  const time = t.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  });
  return { date, time };
}

export const QUALITY_CHIP: Record<
  sessionsApi.CaptureQualityStatus,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' }
> = {
  ok: { label: 'OK', color: 'success' },
  degraded: { label: 'Low rate', color: 'warning' },
  // `bad` now means a sensor that was CONNECTED produced nothing — a sensor
  // nobody clipped on comes back `absent` and doesn't reach this chip (the
  // API re-judges the recording on what was actually there). Before that, an
  // EEG-only recording wore a red "Bad" badge with clean EEG behind it.
  bad: { label: 'Bad', color: 'error' },
  unknown: { label: '—', color: 'default' },
};

/** Per-sensor "device: achieved/target Hz" lines for the tile tooltip —
 *  the device identity + achieved sampling frequency the operator asked to
 *  see (ROADMAP CAPTURE-RATE-MONITOR). */
function formatQualityDetail(quality: sessionsApi.CaptureQuality): string {
  const lines = quality.sensors.map((s) => {
    const dev = s.deviceName ?? s.sensorId;
    // `headlineHz` is the API's one reading of this sensor's rate — the EEG
    // channel for a Muse rather than whichever of EEG/PPG missed target by a
    // hair. Reading `achievedHz` here is what made this tooltip say 63 Hz
    // while the device-health popup said 254 for the same recording. Falls
    // back for a response from an API that predates it.
    const hz = s.headlineHz ?? s.achievedHz;
    const target = s.headlineTargetHz ?? s.targetHz;
    const status = s.headlineStatus ?? s.status;
    const rate =
      status === 'absent'
        ? 'not used'
        : status === 'bad'
          ? 'no data'
          : `${Math.round(hz)}${target ? `/${target}` : ''} Hz`;
    return `• ${dev}: ${rate}${status === 'low' ? ' ⚠' : ''}`;
  });
  return [`Capture quality: ${quality.status}`, ...lines].join('\n');
}

/** Prettify a session kind (live_surgery → "Live surgery"). Kept local
 *  so the component doesn't need a runtime import of the api module. */
function formatSessionKind(kind: string | null): string | null {
  if (!kind) return null;
  const s = kind.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** One-line org context ("General Surgery · OR 4 · Live surgery"), or ''
 *  when nothing is tagged. Names are display-only, resolved by the caller. */
function orgContextLine(event: TimelineEvent): string {
  return [
    event.departmentName ?? undefined,
    event.locationName ?? undefined,
    formatSessionKind(event.sessionKind) ?? undefined,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function formatTooltipDetail(
  event: TimelineEvent,
  timeZone?: string,
): string {
  const when = formatDateTime(event.capturedAt, { timeZone });
  if (event.kind === 'session') {
    const dur =
      event.durationS !== null
        ? event.durationS >= 60
          ? `${(event.durationS / 60).toFixed(1)} min`
          : `${event.durationS} s`
        : '—';
    const org = orgContextLine(event);
    const role = event.roleName ? ` · ${event.roleName}` : '';
    const base =
      `Session — ${event.procedureName ?? 'procedure'}${role} · ${when} · ${dur}` +
      (org ? `\n${org}` : '');
    return event.captureQuality
      ? `${base}\n${formatQualityDetail(event.captureQuality)}`
      : base;
  }
  const dur = event.durationS !== null ? `${event.durationS} s` : '—';
  return `Baseline — ${dur} · captured ${when}`;
}

/**
 * The per-event action handlers a caller can wire. Every one is optional;
 * a surface passes only the actions it offers. Shared by the inline icon
 * column, the popup menu, and {@link hasAnyAction} so the three agree on
 * which actions a given event exposes.
 */
export interface TimelineActionHandlers {
  onDownload?: (e: TimelineEvent) => void;
  onAttach?: (e: TimelineEvent) => void;
  onTag?: (e: TimelineEvent) => void;
  onDisable?: (e: TimelineEvent) => void;
  onRestore?: (e: TimelineEvent) => void;
  onReplay?: (e: TimelineEvent) => void;
  onSplit?: (e: TimelineEvent) => void;
  onViewTrace?: (e: TimelineEvent) => void;
  hideSessionDownload?: boolean;
}

/**
 * Does this event have at least one action to offer in the popup menu?
 * The single source of truth for "is the tile actionable" — gates the
 * left-click (actionMenu) and right-click (contextMenu) popups. Mirrors
 * exactly the conditions in buildMenuItems, so the two can't drift.
 */
export function hasAnyAction(
  event: TimelineEvent,
  h: TimelineActionHandlers,
): boolean {
  const isSession = event.kind === 'session';
  const isDeleted = event.deletedAt !== null;
  return (
    (h.onDownload !== undefined && !(h.hideSessionDownload && isSession)) ||
    (h.onAttach !== undefined && isSession) ||
    (isDeleted && h.onRestore !== undefined) ||
    (!isDeleted && h.onDisable !== undefined) ||
    (h.onReplay !== undefined && isSession && !isDeleted) ||
    (h.onSplit !== undefined &&
      isSession &&
      !isDeleted &&
      event.s3Key !== null) ||
    (h.onTag !== undefined && isSession) ||
    (h.onViewTrace !== undefined && isSession)
  );
}

/** Session ids per tenant, for `useSessionPipelines`. Rows without a tenant
 *  fall under `fallbackTenantId` (the tenant drill-down knows its tenant;
 *  the participant feed carries it per row). */
export function pipelineGroups(
  events: TimelineEvent[],
  fallbackTenantId: string | null = null,
): { tenantId: string; sessionIds: string[] }[] {
  const by = new Map<string, string[]>();
  for (const e of events) {
    if (e.kind !== 'session') continue;
    const s = e.raw as sessionsApi.CaptureSession;
    const tenantId = s.tenant_id ?? fallbackTenantId;
    if (!tenantId || !s.session_id) continue;
    const ids = by.get(tenantId) ?? [];
    ids.push(s.session_id);
    by.set(tenantId, ids);
  }
  return [...by.entries()].map(([tenantId, sessionIds]) => ({ tenantId, sessionIds }));
}

/** Hang each session's pipeline on its tile. */
export function withPipelines(
  events: TimelineEvent[],
  pipelines: Map<string, SessionPipeline>,
): TimelineEvent[] {
  if (pipelines.size === 0) return events;
  return events.map((e) =>
    e.kind === 'session'
      ? {
          ...e,
          pipeline:
            pipelines.get((e.raw as sessionsApi.CaptureSession).session_id) ?? null,
        }
      : e,
  );
}
